import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/errors";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useBillingSummary,
  useCancelSubscription,
  useOrgBillingPortal,
  useSetOrgMaxSpend,
  useStartCheckout,
} from "@/queries/billing";
import { useOrgs } from "@/queries/orgs";
import { openCheckout } from "@/utils/dodo";

function fmtCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(s: string | null): string {
  if (!s) return "-";
  return new Date(s).toLocaleDateString();
}

export function OrgBillingPage() {
  const { orgId } = useParams({ from: "/orgs/$orgId/billing" });
  const orgs = useOrgs();
  const org = orgs.data?.find((o) => o.id === orgId);
  const summary = useBillingSummary(orgId);
  const checkout = useStartCheckout(orgId);
  const cancel = useCancelSubscription(orgId);
  const portal = useOrgBillingPortal(orgId);
  const setMaxSpend = useSetOrgMaxSpend(orgId);
  const [maxSpendInput, setMaxSpendInput] = useState("");

  const isOwner = org?.role === "owner";

  if (orgs.isLoading || summary.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }
  if (org && !isOwner) {
    return (
      <div className="space-y-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/orgs/$orgId" params={{ orgId }}>
            <ArrowLeft className="size-4" />
            Back to projects
          </Link>
        </Button>
        <p className="text-sm text-muted-foreground">
          Only organization owners can view billing.
        </p>
      </div>
    );
  }

  const data = summary.data;
  const onUpgrade = async () => {
    try {
      const res = await checkout.mutateAsync();
      await openCheckout(res.checkout_url);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };
  const onCancel = () => {
    if (!confirm("Cancel Team subscription at end of current period?")) return;
    cancel.mutate(undefined, {
      onSuccess: () => toast.success("Subscription will cancel at period end"),
      onError: (e) => toast.error(getErrorMessage(e)),
    });
  };

  return (
    <div className="space-y-6">
      <Helmet><title>{org?.name ? `TodoLLM - ${org.name} Billing` : "TodoLLM - Billing"}</title></Helmet>
      <div className="space-y-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/orgs/$orgId" params={{ orgId }}>
            <ArrowLeft className="size-4" />
            Back to projects
          </Link>
        </Button>
        <h2 className="text-3xl font-semibold tracking-tight">Billing</h2>
        <p className="text-sm text-muted-foreground">
          Subscription, seats, and task usage for this organization.
        </p>
      </div>

      {data?.read_only && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <p className="font-medium">Billing past due</p>
          <p className="text-destructive/80">
            The last invoice for this organization failed to pay. Task creation
            and new invites are paused until the balance is settled via the
            customer portal below.
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl capitalize">
            {data?.plan ?? "-"} plan
          </CardTitle>
          <CardDescription>
            {data?.plan === "team"
              ? `Current cycle: ${fmtDate(data.billing_cycle_start)} - ${fmtDate(
                  data.billing_cycle_end,
                )}. Billed at cycle end.`
              : "Upgrade to Team to invite teammates. $5 org + $2/seat + $2/month includes 100 tasks, then $0.02/task."}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {data?.plan === "free" ? (
            <Button onClick={onUpgrade} disabled={checkout.isPending}>
              {checkout.isPending ? "Opening checkout..." : "Upgrade to Team"}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={cancel.isPending}
              >
                {cancel.isPending ? "Cancelling..." : "Cancel at period end"}
              </Button>
              {data?.dodo_customer_id && (
                <>
                  <Button
                    variant="outline"
                    disabled={portal.isPending}
                    onClick={async () => {
                      try {
                        const res = await portal.mutateAsync();
                        window.open(
                          res.overview,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      } catch (e) {
                        toast.error(getErrorMessage(e));
                      }
                    }}
                  >
                    {portal.isPending ? "Opening..." : "Manage billing"}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={portal.isPending}
                    onClick={async () => {
                      try {
                        const res = await portal.mutateAsync();
                        const url = res.update_payment_method ?? res.overview;
                        window.open(url, "_blank", "noopener,noreferrer");
                      } catch (e) {
                        toast.error(getErrorMessage(e));
                      }
                    }}
                  >
                    Update payment method
                  </Button>
                  <p className="basis-full text-xs text-muted-foreground">
                    Manage billing opens the Dodo customer portal where you can
                    view this cycle's invoice estimate, seats, task usage, line
                    items, payment method, and past invoices.
                  </p>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {data?.plan === "team" && (
        <Card>
          <CardHeader>
            <CardTitle>Spending cap</CardTitle>
            <CardDescription>
              Set a maximum monthly invoice. New tasks and invites are blocked
              when the projected cycle cost would exceed the cap. Leave blank
              for no cap.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Current cap:{" "}
              <strong>
                {data.max_spend_cents == null
                  ? "no cap"
                  : fmtCents(data.max_spend_cents)}
              </strong>
            </p>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                const raw = maxSpendInput.trim();
                const cents =
                  raw === "" ? null : Math.round(parseFloat(raw) * 100);
                if (cents != null && (Number.isNaN(cents) || cents < 0)) {
                  toast.error("Enter a non-negative dollar amount");
                  return;
                }
                try {
                  await setMaxSpend.mutateAsync(cents);
                  toast.success(cents == null ? "Cap removed" : "Cap updated");
                  setMaxSpendInput("");
                } catch (err) {
                  toast.error(getErrorMessage(err));
                }
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="org-max-spend">Cap ($/month)</Label>
                <Input
                  id="org-max-spend"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder={
                    data.max_spend_cents == null
                      ? "no cap"
                      : (data.max_spend_cents / 100).toFixed(2)
                  }
                  value={maxSpendInput}
                  onChange={(e) => setMaxSpendInput(e.target.value)}
                />
              </div>
              <Button type="submit" disabled={setMaxSpend.isPending}>
                {setMaxSpend.isPending ? "Saving…" : "Save cap"}
              </Button>
              {data.max_spend_cents != null && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={setMaxSpend.isPending}
                  onClick={async () => {
                    try {
                      await setMaxSpend.mutateAsync(null);
                      toast.success("Cap removed");
                      setMaxSpendInput("");
                    } catch (err) {
                      toast.error(getErrorMessage(err));
                    }
                  }}
                >
                  Remove cap
                </Button>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Workspace info</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">
              Organization ID:
            </span>{" "}
            <code>{orgId}</code>
          </p>
          {data?.dodo_customer_id && (
            <p>
              <span className="font-medium text-foreground">Customer id:</span>{" "}
              <code>{data.dodo_customer_id}</code>
            </p>
          )}
          {data?.dodo_subscription_id && (
            <p>
              <span className="font-medium text-foreground">Subscription:</span>{" "}
              <code>{data.dodo_subscription_id}</code>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
